/**
 * React hook wrapping the shared WbAgentSession helpers into a
 * stateful chat interface. All business logic lives in the weave-level
 * WbAgentSession module; this hook only manages React state and effects.
 */

import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import {
  TERMINAL_STATES,
  type TurnResponse,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {
  type ChatMessage,
  extractSteps,
  getMostRecentSessionId,
  getParentTurnId,
  type ProgressStep,
  stripFailedTail,
  type Tab,
  turnsToMessages,
} from '@wandb/weave/lib/WbAgent/WbAgentSession';
import {useCallback, useEffect, useRef, useState} from 'react';

// Re-export shared types and helpers so consumers can import from one place
export type {
  ChatMessage,
  ProgressStep,
  Tab,
} from '@wandb/weave/lib/WbAgent/WbAgentSession';
export {
  extractSteps,
  STATE_LABELS,
  summarizeToolArgs,
} from '@wandb/weave/lib/WbAgent/WbAgentSession';

// ── Formatting helpers (presentation concern, not in shared lib) ────
export const fmtTime = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
  } catch {
    return iso;
  }
};

export const fmtDateGroup = (iso: string): string => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round(
      (today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (diffDays === 0) {
      return 'Today';
    }
    if (diffDays === 1) {
      return 'Yesterday';
    }
    if (diffDays < 7) {
      return d.toLocaleDateString([], {weekday: 'long'});
    }
    return d.toLocaleDateString([], {month: 'short', day: 'numeric'});
  } catch {
    return 'Unknown';
  }
};

const POLL_INTERVAL_MS = 3000;

// =============================================================================
// Hook
// =============================================================================

export interface UseWbAgentChatOptions {
  isOpen: boolean;
  entity: string | null;
  project: string | null;
  /** Called after message list changes that should scroll to bottom. */
  onScrollRequest?: () => void;
}

export interface UseWbAgentChatReturn {
  // State
  available: boolean | null;
  messages: ChatMessage[];
  sessions: TurnResponse[];
  rootTurnId: string | null;
  activeTurnId: string | null;
  activeTab: Tab;
  input: string;
  sending: boolean;

  // Actions
  setInput: (value: string) => void;
  setActiveTab: (tab: Tab) => void;
  send: () => Promise<void>;
  cancel: () => Promise<void>;
  loadSession: (sessionRootTurnId: string) => Promise<void>;
  newConversation: () => void;
}

export function useWbAgentChat({
  isOpen,
  entity,
  project,
  onScrollRequest,
}: UseWbAgentChatOptions): UseWbAgentChatReturn {
  const client = useWbAgentClient(entity ?? undefined, project ?? undefined);

  const [available, setAvailable] = useState<boolean | null>(null);
  const [rootTurnId, setRootTurnId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('messages');
  const [sessions, setSessions] = useState<TurnResponse[]>([]);

  // Stable ref so callbacks don't re-create when onScrollRequest changes
  const scrollRef = useRef(onScrollRequest);
  scrollRef.current = onScrollRequest;
  const scrollToBottom = useCallback(() => scrollRef.current?.(), []);

  // ── Availability ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !client) {
      setAvailable(null);
      return;
    }
    let cancelled = false;
    client
      .get('/is-available')
      .then(r => r.available)
      .catch(() => false)
      .then(a => {
        if (!cancelled) {
          setAvailable(a);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, client]);

  // ── Sessions ──────────────────────────────────────────────────────────
  const fetchSessions = useCallback(async () => {
    if (!client) {
      return;
    }
    try {
      const turns = await client.get('/turns', {
        is_root: true,
        sort_by: 'created_at',
        sort_desc: true,
      });
      setSessions(turns);
    } catch {
      // Ignore fetch errors
    }
  }, [client]);

  // Fetch sessions when drawer opens or history tab is selected
  useEffect(() => {
    if (!isOpen || !client) {
      return;
    }
    fetchSessions();
  }, [isOpen, client, fetchSessions]);

  useEffect(() => {
    if (activeTab === 'history' && client) {
      fetchSessions();
    }
  }, [activeTab, client, fetchSessions]);

  // Auto-load most recently active session on first open
  useEffect(() => {
    if (!isOpen || !client || rootTurnId) {
      return;
    }
    let cancelled = false;
    (async () => {
      const recentId = await getMostRecentSessionId(client);
      if (!cancelled && recentId) {
        loadSession(recentId);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, client]);

  // ── Load session ──────────────────────────────────────────────────────
  const loadSession = useCallback(
    async (sessionRootTurnId: string) => {
      if (!client) {
        return;
      }
      try {
        const turns = await client.get('/turns', {
          root_turn_id: sessionRootTurnId,
          sort_by: 'created_at',
          sort_desc: false,
        });
        const result = turnsToMessages(turns);
        setRootTurnId(sessionRootTurnId);
        setMessages(result.messages);
        setActiveTurnId(result.activeTurnId);
        setActiveTab('messages');
        scrollToBottom();
      } catch {
        // Ignore load errors
      }
    },
    [client, scrollToBottom]
  );

  // ── Polling ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!client || !activeTurnId) {
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const turns = await client.get('/turns', {
          root_turn_id: rootTurnId ?? activeTurnId,
          sort_by: 'created_at',
          sort_desc: false,
        });
        if (cancelled) {
          return;
        }
        const turn = turns.find((t: TurnResponse) => t.id === activeTurnId);
        if (!turn) {
          return;
        }
        const steps = extractSteps(turn);
        if (TERMINAL_STATES.includes(turn.state)) {
          const agentText =
            turn.final_output ?? (turn.state === 'errored' ? 'Error' : 'Done');
          setMessages(prev => {
            const next = prev.filter(m => m.role !== 'loading');
            return [
              ...next,
              ...(steps.length > 0 ? [{role: 'steps' as const, steps}] : []),
              {role: 'agent' as const, text: agentText},
            ];
          });
          setActiveTurnId(null);
          scrollToBottom();
        } else {
          setMessages(prev => {
            const loadingMsg = prev.find(m => m.role === 'loading');
            if (
              loadingMsg &&
              loadingMsg.role === 'loading' &&
              loadingMsg.steps.length === steps.length
            ) {
              // No new steps — skip update to avoid no-op re-render
              return prev;
            }
            return prev.map(m => (m.role === 'loading' ? {...m, steps} : m));
          });
          scrollToBottom();
        }
      } catch {
        // Ignore polling errors
      }
    };
    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [client, activeTurnId, rootTurnId, scrollToBottom]);

  // ── Send ──────────────────────────────────────────────────────────────
  const send = useCallback(async () => {
    if (!client || !input.trim() || sending) {
      return;
    }
    const text = input.trim();
    setInput('');
    setSending(true);
    setActiveTab('messages');

    setMessages(prev => [
      ...stripFailedTail(prev),
      {role: 'user', text},
      {role: 'loading', steps: []},
    ]);
    scrollToBottom();

    try {
      const parentTurnId = rootTurnId
        ? await getParentTurnId(client, rootTurnId)
        : null;

      const newTurn = await client.post('/turns', {
        entity: entity!,
        project: project!,
        user_prompt: text,
        parent_turn_id: parentTurnId,
      });

      if (!rootTurnId) {
        setRootTurnId(newTurn.root_turn_id);
      }
      setActiveTurnId(newTurn.id);
    } catch (err) {
      setMessages(prev => {
        const next = prev.filter(m => m.role !== 'loading');
        return [
          ...next,
          {
            role: 'agent' as const,
            text: `Error: ${err instanceof Error ? err.message : String(err)}`,
          },
        ];
      });
    } finally {
      setSending(false);
      scrollToBottom();
    }
  }, [client, entity, project, input, sending, rootTurnId, scrollToBottom]);

  // ── Cancel ────────────────────────────────────────────────────────────
  const cancel = useCallback(async () => {
    if (!client || !activeTurnId) {
      return;
    }
    try {
      await client.post(
        `/turns/${activeTurnId}/cancel` as '/turns',
        {
          entity: client.entity,
          project: client.project,
        } as never
      );
    } catch {
      // Best-effort cancel
    }
    setMessages(prev => {
      const next = prev.filter(m => m.role !== 'loading');
      return [...next, {role: 'agent' as const, text: 'Cancelled.'}];
    });
    setActiveTurnId(null);
  }, [client, activeTurnId]);

  // ── New conversation ──────────────────────────────────────────────────
  const newConversation = useCallback(() => {
    setRootTurnId(null);
    setMessages([]);
    setActiveTurnId(null);
    setInput('');
    setActiveTab('messages');
  }, []);

  return {
    available,
    messages,
    sessions,
    rootTurnId,
    activeTurnId,
    activeTab,
    input,
    sending,
    setInput,
    setActiveTab,
    send,
    cancel,
    loadSession,
    newConversation,
  };
}
